import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import Button from '../../../../reusable/Button/Button';
import Input from '../../../../reusable/Input/Input';
import { toast } from '../../../../reusable/Toast/toastStore';
import {
  useCreateSuperAffiliatePromoCodeMutation,
  useLazyCheckPromoCodeAvailabilityQuery,
} from '../../../../store/api/affiliateApi';
import {
  CODE_HINTS,
  CODE_LABELS,
  CODE_TYPES,
  apiMessage,
  codeKeysFor,
  codeTerms,
  codeValue,
  promoCodeEntry,
  rulesFor,
  validateCode,
} from './promoCodes';
import { t } from '../../../../i18n/i18n';

/** A claimed code as a row — here, and in the Overview's promo codes card. */
export function ClaimedCode({ label, entry, rules }) {
  const terms = codeTerms(entry, rules);
  return (
    <div className="sa-code is-claimed">
      <div className="sa-code__meta">
        <span className="sa-code__label">{label}</span>
        <span className="sa-code__terms">
          {t('{{discount}}% off · {{commission}}% commission', {
            discount: terms.discount,
            commission: terms.commission,
          })}
        </span>
      </div>
      <span className="sa-code__value">{codeValue(entry)}</span>
    </div>
  );
}

function CodeRow({ codeKey, rules, claimed, onClaimed }) {
  const [code, setCode] = useState('');
  // null until checked, then { usable, message }.
  const [availability, setAvailability] = useState(null);
  const [checkCode, { isFetching: checking }] = useLazyCheckPromoCodeAvailabilityQuery();
  const [claimCode, { isLoading: saving }] = useCreateSuperAffiliatePromoCodeMutation();

  const validationError = useMemo(() => (code ? validateCode(code, rules) : null), [code, rules]);

  // Editing the code voids the last answer.
  useEffect(() => setAvailability(null), [code]);

  if (codeValue(claimed)) {
    return <ClaimedCode label={t(CODE_LABELS[codeKey]) || codeKey} entry={claimed} rules={rules} />;
  }

  const check = async () => {
    if (checking || validationError) return;
    try {
      const result = await checkCode({ code, codeType: CODE_TYPES[codeKey] }).unwrap();
      // The endpoint always answers 200 — both flags have to pass.
      const usable = Boolean(result?.valid) && Boolean(result?.available);
      setAvailability({
        usable,
        message:
          result?.message ||
          result?.detail ||
          (usable
            ? t('Code is available')
            : result?.valid === false
              ? t('That code does not match the required format')
              : t('That code is already taken')),
      });
    } catch (err) {
      setAvailability({ usable: false, message: apiMessage(err, t('Could not check that code. Please try again.')) });
    }
  };

  const claim = async () => {
    if (saving || validationError || !availability?.usable) return;
    try {
      const response = await claimCode({ code_type: CODE_TYPES[codeKey], code }).unwrap();
      toast.success(t('{{label}} claimed', { label: t(CODE_LABELS[codeKey]) }));
      onClaimed(codeKey, codeValue(response) || code);
    } catch (err) {
      toast.error(t('Could not claim that code'), apiMessage(err, t('Please try again.')));
    }
  };

  const note = code && validationError ? validationError : availability?.message;
  const noteTone = code && validationError ? 'error' : availability?.usable ? 'ok' : 'error';

  return (
    <div className="sa-code">
      <div className="sa-code__meta">
        <span className="sa-code__label">{t(CODE_LABELS[codeKey]) || codeKey}</span>
        <span className="sa-code__terms">
          {t('{{discount}}% off · {{commission}}% commission', {
            discount: rules?.discount_percent,
            commission: rules?.commission_percent,
          })}
        </span>
        {CODE_HINTS[codeKey] && <span className="sa-code__hint">{t(CODE_HINTS[codeKey])}</span>}
      </div>

      <div className="sa-code__form">
        <Input
          variant="form"
          value={code}
          placeholder={t('e.g. {{example}}', { example: rules?.example || 'LK' })}
          maxLength={rules?.max_length || 16}
          // Codes are stored uppercase; the field keeps to it.
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          // Red outline only: the message sits under the whole row, buttons included.
          status={code && validationError ? 'error' : ''}
          aria-label={t('{{label}} code', { label: t(CODE_LABELS[codeKey]) })}
        />
        <Button variant="ghost" height={40} disabled={checking || !code || Boolean(validationError)} onClick={check}>
          {checking ? t('Checking…') : t('Check')}
        </Button>
        <Button variant="submit" height={40} disabled={saving || !availability?.usable} loading={saving} onClick={claim}>
          {t('Claim')}
        </Button>
      </div>

      {note && <small className={`sa-code__note is-${noteTone}`}>{note}</small>}
    </div>
  );
}

/**
 * Set Up Your Promo Codes — the pop-up live shows a newly approved Super
 * Affiliate until both codes are claimed (`PromoCodeSetup.jsx` on main).
 */
export default function PromoCodeSetupDialog({ open, superAffiliate, onClose }) {
  // Codes claimed here, before the dashboard's refetch brings them back.
  const [localCodes, setLocalCodes] = useState({});

  const keys = codeKeysFor(superAffiliate);
  const claimedFor = (key) => promoCodeEntry(superAffiliate, key) || localCodes[key] || null;
  const allClaimed = keys.every((key) => codeValue(claimedFor(key)));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={620}
      destroyOnHidden
      className="aff-dialog"
      rootClassName="aff-dialog-root"
      aria-label={t('Set up your promo codes')}
    >
      <div className="sa-setup">
        <h2 className="aff-dialog__title">{t('Set Up Your Promo Codes')}</h2>
        <p className="aff-dialog__intro">
          {t('You have been approved as a Super Affiliate. Choose your two codes — each one can only be claimed once.')}
        </p>

        {keys.map((key) => (
          <CodeRow
            key={key}
            codeKey={key}
            rules={rulesFor(superAffiliate, key)}
            claimed={claimedFor(key)}
            onClaimed={(codeKey, value) => setLocalCodes((prev) => ({ ...prev, [codeKey]: value }))}
          />
        ))}

        <div className="aff-dialog__actions">
          <Button variant={allClaimed ? 'submit' : 'ghost'} height={38} onClick={onClose}>
            {allClaimed ? t('Done') : t('I’ll do this later')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
